import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Compass, ArrowLeft, ArrowRight, X, Check, PhoneCall } from "lucide-react";

// Demo-only guided walkthrough. Points a first-time prospect at the key screens for their role.
// Renders nothing for real (non-demo) users. Dismissal is remembered per role in localStorage.
const STEPS = {
  admin: [
    { title: "Welcome to the ARPS demo", body: "You're in the full Admin portal, running on sample data. Take a 30-second tour of the key screens." },
    { target: "nav-command-center", title: "Command Center", body: "Your live operations overview — urgent requests, approvals, payouts and anything that needs attention." },
    { target: "nav-all-jobs", title: "All Jobs", body: "Every service order with its status, assigned server, and GPS-verified attempts." },
    { target: "nav-clients", title: "Clients", body: "The law firms you serve, their orders, and onboarding status." },
    { target: "nav-process-servers", title: "Process Servers", body: "Your field provider roster, eligibility badges, and document approvals." },
    { target: "nav-payouts", title: "Payouts", body: "Review and release provider compensation once a job is completed and paid." },
    { target: "nav-demo-environments", title: "Demo Environments", body: "Generate fresh, isolated demo sandboxes like this one for any prospect." },
    { cta: true, title: "Ready to run it on your caseload?", body: "This is the real platform on sample data. Let's set up your firm's account and get your first serve on the record." },
  ],
  client: [
    { title: "Welcome to the ARPS demo", body: "You're in the Attorney / Client portal on sample data. Here's a quick look around." },
    { target: "nav-dashboard", title: "Your dashboard", body: "Track every service order at a glance — active, completed, and attempts logged." },
    { target: "nav-new-service-order", title: "Start a service", body: "Request a new process serve in minutes with a live price quote." },
    { target: "nav-service-history", title: "Service History", body: "Download proof of service and revisit every past job." },
    { target: "nav-billing", title: "Billing", body: "See invoices and payment status for all your orders." },
    { cta: true, title: "Like what you see?", body: "Let's get your firm set up so your next serve is GPS-verified and court-ready from day one." },
  ],
  server: [
    { title: "Welcome to the ARPS demo", body: "You're in the Process Server portal on sample data. Here's a quick tour." },
    { target: "nav-assignments", title: "Assignments", body: "Accept available jobs and log GPS-verified service attempts from the field." },
    { target: "nav-job-history", title: "Job History", body: "Every job you've completed, with its evidence chain and returns." },
    { target: "nav-payouts", title: "Payouts", body: "Track your earnings and cash out completed compensation." },
    { cta: true, title: "Want to serve on ARPS?", body: "Let's talk about getting you onboarded so you can start picking up GPS-verified assignments." },
  ],
};

export function DemoTour() {
  const { user } = useAuth();
  const role = user?.role;
  const steps = STEPS[role] || [];
  const storageKey = `arps_demo_tour_${role}`;
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (user?.is_demo && steps.length && !localStorage.getItem(storageKey)) {
      const t = setTimeout(() => { setI(0); setActive(true); }, 900);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user, storageKey, steps.length]);

  const measure = useCallback(() => {
    const step = steps[i];
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) { setRect(r); return; }
    }
    setRect(null);
  }, [i, steps]);

  useEffect(() => {
    if (!active) return undefined;
    measure();
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [active, i, measure]);

  if (!user?.is_demo || !steps.length) return null;

  if (!active) {
    return (
      <button
        data-testid="demo-tour-launch"
        onClick={() => { setI(0); setActive(true); }}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
      >
        <Compass className="h-4 w-4" /> Guided tour
      </button>
    );
  }

  const step = steps[i];
  const last = i === steps.length - 1;
  const finish = (done) => { setActive(false); localStorage.setItem(storageKey, done ? "done" : "skipped"); };

  const card = 340;
  const cardStyle = rect
    ? { top: Math.max(12, Math.min(rect.top, window.innerHeight - 260)), left: Math.min(rect.right + 16, window.innerWidth - card - 12) }
    : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

  return (
    <div data-testid="demo-tour" className="fixed inset-0 z-[60]">
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-xl ring-2 ring-emerald-400 transition-all duration-200"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: "0 0 0 9999px rgba(15,23,42,0.62)" }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/62" />
      )}

      <div
        data-testid="demo-tour-card"
        className="absolute w-[340px] max-w-[calc(100vw-24px)] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl"
        style={cardStyle}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
            <Compass className="h-3 w-3" /> Demo tour
          </span>
          <button data-testid="demo-tour-skip" onClick={() => finish(false)} className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">{step.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.body}</p>
        {step.cta ? (
          <div className="mt-4 space-y-2">
            <button
              data-testid="demo-tour-book-call"
              onClick={() => window.open("/contact", "_blank", "noopener")}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600"
            >
              <PhoneCall className="h-4 w-4" /> Book a call
            </button>
            <button
              data-testid="demo-tour-explore"
              onClick={() => finish(true)}
              className="w-full rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition hover:bg-slate-100"
            >
              Keep exploring on my own
            </button>
          </div>
        ) : (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400">{i + 1} of {steps.length}</span>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button data-testid="demo-tour-back" onClick={() => setI((v) => v - 1)}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            {last ? (
              <button data-testid="demo-tour-done" onClick={() => finish(true)}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600">
                <Check className="h-4 w-4" /> Done
              </button>
            ) : (
              <button data-testid="demo-tour-next" onClick={() => setI((v) => v + 1)}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
